import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { qd, money, apiError } from '../api';
import { useQdAuth } from '../QdApp';

const EMPTY = { ref: '', name: '', category: 'Sensor', price: 0, active: true };
const CATS = ['All', 'Gateway', 'Sensor', 'Accessory', 'Services', 'Software'];

export default function QdCatalog() {
  const { user } = useQdAuth();
  const isAdmin = ['admin', 'superadmin'].includes(user.role) || !!user.can_edit_products;
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('All');

  const load = () => qd.get('/products').then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const shown = products.filter((p) => {
    if (cat !== 'All' && p.category !== cat) return false;
    const q = search.trim().toLowerCase();
    return !q || p.ref.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
  });

  const save = async () => {
    setMsg('');
    try {
      const body = { ...form, price: Number(form.price) || 0 };
      if (form.id) await qd.patch(`/products/${form.id}`, body);
      else await qd.post('/products', body);
      setForm(null); load();
    } catch (e) { setMsg(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#14233F]">Catalog</h1>
        {isAdmin && <button onClick={() => setForm({ ...EMPTY })} className="bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-new-product-btn">New product</button>}
      </div>
      <p className="text-xs text-slate-400 mb-4">{isAdmin ? 'Prices are editable here and used live in quotes. Excel import coming with the price list file.' : 'Read-only — contact an admin to change prices.'}</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ref or name…"
            className="border border-slate-300 rounded pl-9 pr-3 py-2 text-sm w-64" data-testid="qd-catalog-search" />
        </div>
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-3 py-1.5 text-xs font-medium rounded border ${cat === c ? 'bg-[#14233F] text-white border-[#14233F]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            data-testid={`qd-cat-filter-${c.toLowerCase()}`}>
            {c}
            <span className="ml-1.5 mono text-[10px] opacity-60">
              {c === 'All' ? products.length : products.filter((p) => p.category === c).length}
            </span>
          </button>
        ))}
        {(search || cat !== 'All') && (
          <span className="text-xs text-slate-400 ml-1" data-testid="qd-catalog-count">{shown.length} result{shown.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {form && isAdmin && (
        <div className="bg-white border border-slate-200 rounded p-5 mb-6 flex flex-wrap gap-3 items-center" data-testid="qd-product-form">
          <input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="Reference *" className="border border-slate-300 rounded px-3 py-2 text-sm mono w-36" data-testid="qd-product-ref" />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Designation *" className="border border-slate-300 rounded px-3 py-2 text-sm flex-1 min-w-[200px]" data-testid="qd-product-name" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-slate-300 rounded px-3 py-2 text-sm">
            {['Gateway', 'Sensor', 'Accessory', 'Services', 'Software'].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price €" className="border border-slate-300 rounded px-3 py-2 text-sm mono w-28 text-right" data-testid="qd-product-price" />
          <label className="text-xs text-slate-500 flex items-center gap-1.5">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <button onClick={save} className="bg-[#14233F] text-white px-4 py-2 rounded text-sm" data-testid="qd-product-save">Save</button>
          <button onClick={() => setForm(null)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600">Cancel</button>
          {msg && <span className="text-xs text-red-600">{msg}</span>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">Ref</th>
              <th className="text-left px-4 py-3">Designation</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Price (excl. VAT)</th>
              <th className="text-left px-4 py-3">Status</th>
              {isAdmin && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!p.active ? 'opacity-40' : ''}`}>
                <td className="px-4 py-3 mono text-xs">{p.ref}</td>
                <td className="px-4 py-3 text-slate-800">{p.name}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{p.category}</td>
                <td className="px-4 py-3 mono text-right font-semibold">{money(p.price)}</td>
                <td className="px-4 py-3 text-xs">{p.active ? 'Active' : 'Inactive'}</td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setForm(p)} className="text-xs text-[#0F7D70] hover:underline" data-testid={`qd-product-edit-${p.ref}`}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-4 py-10 text-center text-slate-400 text-sm">No products match your filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
